// Rhythmic entries, rather than arbitrary SVG glyphs, define the grading gaps.
const CALIBRATED_LEVELS = new Set([
  1,
  2,
  4,
  5,
  6,
  "1p",
  "2p",
  "3p",
  "4p",
  "5p",
  "6p",
  "4pSp26",
  "5pSp26",
]);
export function barlineSlotOffset(config) {
  if (
    Number.isInteger(config.barlineSlotOffset) &&
    config.barlineSlotOffset >= 0
  )
    return config.barlineSlotOffset;
  // The old tables counted the clef and time signature as their first two
  // entries. Preserve those existing answer IDs without drawing header gaps.
  return CALIBRATED_LEVELS.has(config.level) ? 2 : 0;
}

function svgPoint(svg, element, x, y) {
  const outer = svg.getScreenCTM();
  const inner = element.getScreenCTM();
  if (!outer || !inner) return null;
  const point = svg.createSVGPoint();
  point.x = x;
  point.y = y;
  return point.matrixTransform(outer.inverse().multiply(inner));
}

export function collectBarlineEntries(osmd) {
  const entries = [];
  for (const page of osmd.GraphicSheet?.MusicPages || []) {
    for (const system of page.musicSystems || page.MusicSystems || []) {
      for (const group of system.graphicalMeasures ||
        system.GraphicalMeasures ||
        []) {
        // These questions use the first staff; simultaneous voices share one entry.
        const measure = group?.[0];
        for (const entry of measure?.staffEntries || []) {
          let vf = null;
          for (const voice of entry.graphicalVoiceEntries || []) {
            vf =
              voice.vfStaveNote ||
              voice.notes?.find((note) => note.vfnote?.[0])?.vfnote?.[0];
            if (vf?.getStave?.()) break;
          }
          const element = vf?.getSVGElement?.() || vf?.attrs?.el;
          const svg = element?.closest?.("svg");
          const stave = vf?.getStave?.();
          if (!svg || !stave) continue;
          const begin = vf.getNoteHeadBeginX?.() ?? vf.getAbsoluteX?.();
          const end = vf.getNoteHeadEndX?.() ?? begin;
          const x = (begin + end) / 2;
          const top = svgPoint(svg, element, x, stave.getYForLine(0));
          const bottom = svgPoint(svg, element, x, stave.getYForLine(4));
          const staffEnd = svgPoint(
            svg,
            element,
            stave.getNoteEndX(),
            stave.getYForLine(0),
          );
          if (
            !top ||
            !bottom ||
            !staffEnd ||
            ![top.x, top.y, bottom.y, staffEnd.x].every(Number.isFinite)
          )
            continue;
          entries.push({
            svg,
            system,
            x: top.x,
            topY: top.y,
            bottomY: bottom.y,
            staffEndX: staffEnd.x,
          });
        }
      }
    }
  }
  return entries;
}

export function buildBarlineSlots(entries, config) {
  const slots = [],
    offset = barlineSlotOffset(config);
  for (let i = 0; i < entries.length - 1; i++) {
    const entry = entries[i],
      next = entries[i + 1];
    const sameLine = entry.svg === next.svg && entry.system === next.system;
    slots.push({
      ...entry,
      id: `gap_${i + offset}`,
      x: sameLine
        ? (entry.x + next.x) / 2
        : Math.max(entry.x + 8, entry.staffEndX - 4),
    });
  }
  if (config.testEndBarline && entries.length) {
    const entry = entries[entries.length - 1];
    slots.push({
      ...entry,
      id: "end",
      x: Math.max(entry.x + 8, entry.staffEndX - 4),
    });
  }
  return slots;
}
